<template>
    <!-- One end of the street. It has no box of its own: the whole column is
         the target, and what tells the player it is one is that the street
         lights up under the pointer. -->
    <button
        type="button"
        class="pl-fork__end"
        :class="[`pl-fork__end--${side}`, {'is-lit': lit, 'is-chosen': chosen}]"
        :aria-pressed="chosen"
        @click="$emit('choose', path.id)"
        @mouseenter="$emit('enter')"
        @mouseleave="$emit('leave')"
        @focus="$emit('enter')"
        @blur="$emit('leave')"
    >
        <span class="pl-fork__title">
            <span class="pl-fork__mark" aria-hidden="true">
                <img
                    v-if="path.icon"
                    :src="path.icon"
                    class="pl-fork__sigil"
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
                <span v-else class="pl-avatar pl-avatar--md pl-avatar--monogram">{{ monogram }}</span>
            </span>

            <span class="pl-fork__name">{{ text.name }}</span>

            <!-- The stamp, not a chip. It sits in the name's own line because
                 it is a fact about this order, not a badge floating over it. -->
            <span v-if="chosen" class="pl-fork__chosen">{{ t('player.creation.crossroads.chosen') }}</span>
        </span>

        <!-- Held open at rest so nothing under the pointer moves when the order
             speaks; only the ink arrives. -->
        <span class="pl-fork__creed">{{ creed }}</span>
    </button>
</template>

<script setup>
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import {monogramOf} from '@/components/Player/characterPresentation.js';
import {cardTextOf} from '@/components/Player/Creation/cardText.js';

const props = defineProps({
    path: {type: Object, required: true},
    side: {type: String, default: 'flow'},
    lit: {type: Boolean, default: false},
    chosen: {type: Boolean, default: false},
});

defineEmits(['choose', 'enter', 'leave']);

const {t, te} = useI18n();

const text = computed(() => cardTextOf(t, te, 'path', props.path));

/* What the order believes, in one line. Keyed off the side rather than the row,
   because it is written copy about the two orders rather than the GM-authored
   description the API returns — which is a mechanical summary ("a path focused
   on magical abilities") and reads as a tooltip, not as a creed. The row's own
   description remains the caption everywhere else the path appears. */
const creed = computed(() => t(`player.creation.crossroads.creed.${props.side}`));

/* Deliberately the English name: the monogram is the fallback mark when the
   artwork is missing, and "Ш" for both orders would tell the player nothing. */
const monogram = computed(() => monogramOf(props.path.name));
</script>
